import SimpleDate from 'app/common/classes/simple-date'
import toFixedValue from 'app/common/lib/toFixedValue'
import { Brand } from 'app/common/types/brand'
import { ReportFinancesDataReplenishmentSummary } from 'app/dataTypes/reports/data/common/financesSummaryReplenishment'

type DateKey = Brand<string, 'DateKey'>
type SubjectId = string | 'total' | 'Сотрудники'
type Item = { sum: number; count: number }
type Mark = { mark: boolean }

type ApiResultStudents = {
  by_students: true
  human_year: string
  days: Record<DateKey, Record<SubjectId, Item & Mark>>
  subjects: Record<SubjectId, { title: string; klass: string; total: Item }>
  total: Item
}

type ApiResultSchools = {
  by_students: false
  human_year: string
  days: Record<DateKey, Record<SubjectId, Item>>
  subjects: Record<SubjectId, { title: string; total: Item }>
  total: Item
}

type ApiResultClasses = ApiResultSchools

export type ApiResultFinancesSummaryReplenishment =
  | ApiResultStudents
  | ApiResultSchools
  | ApiResultClasses

const createTotal = (res: ApiResultFinancesSummaryReplenishment) => {
  const { total, days: daysData } = res
  const days = Object.keys(daysData) as Array<DateKey>

  return {
    value: toFixedValue(total.sum),
    totalByDateIndex: days.map((day) => ({
      value: toFixedValue(daysData[day].total.sum),
    })),
  }
}

const createDates = (
  res: ApiResultFinancesSummaryReplenishment,
  createDateFromKey: (key: DateKey) => SimpleDate,
) => {
  const { days: daysData } = res
  const days = Object.keys(daysData) as Array<DateKey>
  return days.map((day) => createDateFromKey(day))
}

const createStudents = (res: ApiResultFinancesSummaryReplenishment) => {
  const { by_students: byStudents, days: daysData, subjects: subjectsData } = res
  if (!byStudents) return []
  const days = Object.keys(daysData) as Array<DateKey>
  const subjects = Object.keys(subjectsData)

  return subjects
    .map((id) => ({
      id,
      studentName: subjectsData[id].title,
      studentClassTitle: subjectsData[id].klass,
      valuesByDateIndex: days.map((day) => ({
        value: toFixedValue(daysData[day][id].sum),
      })),
      total: {
        value: toFixedValue(subjectsData[id].total.sum),
      },
    }))
    .sort((a, b) => a.studentName.localeCompare(b.studentName))
}

const createCommon = (res: ApiResultFinancesSummaryReplenishment) => {
  const { by_students: byStudents, days: daysData, subjects: subjectsData } = res
  if (byStudents) return []
  const days = Object.keys(daysData) as Array<DateKey>
  const subjects = Object.keys(subjectsData)

  return subjects
    .map((id) => ({
      id,
      title: subjectsData[id].title,
      valuesByDateIndex: days.map((day) => ({
        value: toFixedValue(daysData[day][id].sum),
      })),
      total: {
        value: toFixedValue(subjectsData[id].total.sum),
      },
    }))
    .sort((a, b) => {
      const aNum = parseInt(a.title, 10)
      const bNum = parseInt(b.title, 10)
      if (isNaN(aNum) || isNaN(bNum) || aNum === bNum) return a.title.localeCompare(b.title)
      return aNum - bNum
    })
}

const createDateFromDayKey = (key: DateKey) => new SimpleDate(key, 'YYYY-MM-DD')
const createDateFromMonthKey = (years: string) => (key: DateKey) => {
  const match = years.match(/(\d{4})\D+(\d{4})/)
  const [yearFrom, yearTo] = match ? match.slice(1) : [2000, 2001]
  const fullDate =
    parseInt(key, 10) > 7
      ? `${yearFrom}-${key.padStart(2, '0')}-01`
      : `${yearTo}-${key.padStart(2, '0')}-01`
  return new SimpleDate(fullDate, 'YYYY-MM-DD')
}

const extractPeriodType = (res: ApiResultFinancesSummaryReplenishment) => {
  const { days } = res
  const keys = Object.keys(days)
  return keys.length > 0 && keys[0].length < 3 ? 'monthly' : 'daily'
}

export const convertResultFinancesSummaryReplenishment = (
  res: ApiResultFinancesSummaryReplenishment,
): ReportFinancesDataReplenishmentSummary => {
  const { by_students: byStudents, human_year: humanYear } = res
  const periodType = extractPeriodType(res)
  const createDateFromKey =
    periodType === 'daily' ? createDateFromDayKey : createDateFromMonthKey(humanYear)
  const dates = createDates(res, createDateFromKey)

  return byStudents
    ? {
        type: 'students',
        periodType,
        dates,
        items: createStudents(res),
        total: createTotal(res),
      }
    : {
        type: 'common',
        periodType,
        dates,
        items: createCommon(res),
        total: createTotal(res),
      }
}
